import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { InfoTooltipIcon } from '@resistapp/client/components/icons/info-tooltip-icon';
import {
  type LabPrimerPrepCompactedRow,
  type LabPrimerPrepOpentronsRow,
  type LabWorkbookSummary,
} from '@resistapp/common/api-types';
// import type { GenePanelConfigurationId } from '@resistapp/common/gene-panels';
import { LabWorkbookStatus, OrganisationType, OrganisationUserRole } from '@resistapp/common/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ResistomapSwitch } from '@resistapp/client/components/bars/filter-bar/resistomap-switch';
import { ReadOnlyTable, type TableColumn } from '@resistapp/client/components/tables/read-only-table';
import { isResistomapAdminUser } from '@resistapp/client/utils/access';
import { useUser } from '../../../contexts/use-user-context';
import { useToastWithoutDebounce } from '../../../hooks/use-resistomap-toast';
import { assignLabWorkbook, patchLabWorkbook } from '../api/update-lab-workbook';
import { useLabAssignmentOptions } from '../api/use-lab-assignment-options';
import { LabPageHeader } from '../components/lab-page-header';
import { SmartChipGridView } from '../components/smartchip-grid';
import { useLabLayoutContext } from '../lab-layout';
import { isStatusAtLeast } from '../status-utils';
import {
  buildPrimerPrepUpdatePayload,
  toPrimerPrepComparableValues,
  type PrimerPrepFormValues,
} from './primer-prep-payload';
import { shouldRequirePrimerStorageForAssignment } from './primer-prep-page-state-utils';

function buildOpentronsCsvForRows(
  rows: LabPrimerPrepOpentronsRow[],
  mode: 'standard' | 'flex',
): { csv: string; suggestedFileName: string } {
  const baseName = mode === 'standard' ? 'primer-prep-opentrons' : 'primer-prep-opentrons-flex';
  const columns =
    mode === 'standard'
      ? [
          'source_labware',
          'source_plate',
          'source_well',
          'source_aspiration_height_above_bottom (in mm)',
          'destination_labware',
          'destination_slot',
          'destination_well',
          'volume',
          '(note: keep volume formula consistent with Master sheet)',
        ]
      : [
          'source_labware',
          'source_plate',
          'source_well',
          'source_aspiration_height_above_bottom (in mm)',
          'destination_labware',
          'destination_slot',
          'destination_well',
          'volume',
        ];

  const escapeCsv = (value: string | number | null | undefined) => {
    if (value == null) return '';
    const s = String(value);
    // Escape quotes and wrap in quotes if any special chars present
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines: string[] = [];
  lines.push(columns.join(','));
  for (const row of rows) {
    const values: Array<string | number | null> = [
      row.sourceLabware,
      row.sourcePlate ?? '',
      row.sourceWell ?? '',
      row.sourceAspirationHeightMm,
      row.destinationLabware,
      row.destinationSlot,
      row.destinationWell,
      row.volume != null ? Number(row.volume).toFixed(2) : '',
    ];
    if (mode === 'standard') {
      values.push(''); // note column left blank
    }
    lines.push(values.map(escapeCsv).join(','));
  }

  return { csv: `${lines.join('\n')}\n`, suggestedFileName: `${baseName}.csv` };
}

function cloneFormValues(values: PrimerPrepFormValues): PrimerPrepFormValues {
  return {
    prescreening: values.prescreening,
    oneHealthOverride: values.oneHealthOverride,
    numChips: values.numChips,
    defaultMmLot: values.defaultMmLot,
    primerStorageFindLocation: values.primerStorageFindLocation,
    primerStorageLeaveLocation: values.primerStorageLeaveLocation,
  };
}

// Runs are grouped dynamically by deck-slot constraints.

function mapSummaryToForm(summary: LabWorkbookSummary): PrimerPrepFormValues {
  return {
    prescreening: summary.workbook.prescreening,
    oneHealthOverride: summary.workbook.oneHealthOverride,
    numChips: summary.workbook.numChips,
    defaultMmLot: summary.workbook.defaultMmLot ?? '',
    primerStorageFindLocation: summary.workbook.primerStorageFindLocation ?? '',
    primerStorageLeaveLocation: summary.workbook.primerStorageLeaveLocation ?? '',
  };
}

function normalizeText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeNumChips(value: number): number {
  if (!Number.isFinite(value) || value < 1) {
    return 1;
  }
  return Math.round(value);
}

type OpentronsRunGroup = {
  label: string;
  rowIndices: number[];
};

function formatPlateRunLabel(plates: number[]): string {
  if (plates.length === 0) {
    return 'Plate run';
  }
  const sorted = [...plates].sort((a, b) => a - b);
  if (sorted.length === 1) {
    return `Plate ${sorted[0]}`;
  }
  if (sorted.length === 2) {
    return `Plates ${sorted[0]} & ${sorted[1]}`;
  }
  const leading = sorted.slice(0, -1).join(', ');
  const trailing = sorted[sorted.length - 1];
  return `Plates ${leading} & ${trailing}`;
}

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

// Deprecated: replaced by SmartChipGridView with banded coloring

export function PrimerPrepPage() {
  const { workbookQuery, workbookId, viewerAccess, readOnly } = useLabLayoutContext();
  const isPrimerPrepReadOnly = readOnly.primerPrep;
  const { data, error, isLoading, isError } = workbookQuery;
  const { user } = useUser();
  const assignmentOptionsQuery = useLabAssignmentOptions(workbookId, { enabled: Boolean(workbookId) });
  const assignedLabOrganisation = data?.assignedLabOrganisation ?? null;
  const assignedLabOrganisationId = assignedLabOrganisation?.id ?? data?.workbook.assignedLabOrganisationId ?? null;
  const workbook = data?.workbook;
  const primerPrep = data?.primerPrep;
  const sampleSheet = data?.sampleSheet;
  const smartChip = data?.smartChip;
  const existingChipCount = smartChip?.chips.length ?? 0;
  const minimumNumChips = Math.max(1, existingChipCount);
  const workbookStatus = workbook?.status ?? null;
  const isLegacy = workbookStatus === LabWorkbookStatus.LEGACY;
  const isPostPrimerPrep = isStatusAtLeast(workbookStatus, LabWorkbookStatus.PRIMERS_PREPPED);
  const canAdminOverrideNumChips =
    isPostPrimerPrep && !isLegacy && (viewerAccess.isResistomapAdmin || viewerAccess.isProviderAdmin);
  const canEditNumChips = !isPrimerPrepReadOnly || canAdminOverrideNumChips;
  const canEditPrimerPrepForm = !isPrimerPrepReadOnly || canAdminOverrideNumChips;
  const assignmentOptions = assignmentOptionsQuery.data?.assignableLabOrganisations ?? [];
  const assignmentOptionsLoading = assignmentOptionsQuery.isLoading;
  const assignmentOptionsError = assignmentOptionsQuery.isError
    ? assignmentOptionsQuery.error instanceof Error
      ? assignmentOptionsQuery.error.message
      : String(assignmentOptionsQuery.error)
    : null;
  const controllingOrganisationId = data?.controllingOrganisation?.id ?? null;
  const canManageAssignment = useMemo(() => {
    if (!user) {
      return false;
    }
    if (isResistomapAdminUser(user)) {
      return true;
    }
    if (!controllingOrganisationId) {
      return false;
    }
    return user.organisations.some(
      organisation =>
        organisation.id === controllingOrganisationId &&
        organisation.type === OrganisationType.PROVIDER &&
        organisation.membershipRole === OrganisationUserRole.ADMIN,
    );
  }, [controllingOrganisationId, user]);
  const isOneHealthDefaultPanel = data?.linkedGenePanel?.defaultKey === 'ONE_HEALTH_96';
  const queryClient = useQueryClient();
  const showToast = useToastWithoutDebounce();
  const initialFormValues = useMemo(() => (data ? mapSummaryToForm(data) : null), [data]);
  const autoPromotionAttemptRef = useRef<number | null>(null);
  const assignLabMutation = useMutation<LabWorkbookSummary, unknown, number | null>({
    mutationFn: async (labOrganisationId: number | null) => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      return await assignLabWorkbook(workbookId, { labOrganisationId });
    },
    onSuccess: summary => {
      queryClient.setQueryData(['labWorkbook', workbookId], summary);
      showToast({ status: 'success', title: 'Updated lab assignment' });
    },
    onError: mutationError => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to update lab assignment';
      showToast({ status: 'error', title: message });
    },
  });
  const { control, handleSubmit, reset, register, watch } = useForm<PrimerPrepFormValues>({
    defaultValues: initialFormValues ?? undefined,
  });

  const lastSubmittedValuesRef = useRef<PrimerPrepFormValues | null>(
    initialFormValues ? cloneFormValues(initialFormValues) : null,
  );
  const lastFailedValuesRef = useRef<PrimerPrepFormValues | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const updateWorkbookMutation = useMutation<LabWorkbookSummary, unknown, PrimerPrepFormValues>({
    mutationFn: async (values: PrimerPrepFormValues) => {
      const normalizedNumChips = normalizeNumChips(values.numChips);
      if (normalizedNumChips < minimumNumChips) {
        throw new Error(`Chip count cannot be lower than uploaded chips (${minimumNumChips}).`);
      }
      const payload = buildPrimerPrepUpdatePayload({ ...values, numChips: normalizedNumChips }, { isPrimerPrepReadOnly });
      return await patchLabWorkbook(workbookId, payload);
    },
    onMutate: () => {
      setSaveErrorMessage(null);
    },
    onSuccess: summary => {
      const mapped = mapSummaryToForm(summary);
      queryClient.setQueryData(['labWorkbook', workbookId], summary);
      lastSubmittedValuesRef.current = cloneFormValues(mapped);
      reset(mapped);
      lastFailedValuesRef.current = null;
      setSaveErrorMessage(null);
    },
    onError: (mutationError, variables) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to update workbook';
      // Remember the last failed values (deep copy) to avoid retrying the same payload repeatedly
      try {
        lastFailedValuesRef.current = JSON.parse(JSON.stringify(variables)) as PrimerPrepFormValues;
      } catch {
        // Fallback to shallow copy if JSON serialization fails for any reason
        lastFailedValuesRef.current = { ...variables };
      }
      setSaveErrorMessage(message);
      showToast({ status: 'error', title: message });
    },
  });

  // Track unsaved edits for warnings + UI indicators.
  const watchedValues = watch();
  const comparableWatchedValues = toPrimerPrepComparableValues(watchedValues, { isPrimerPrepReadOnly });
  const comparableLastSubmittedValues = toPrimerPrepComparableValues(lastSubmittedValuesRef.current, {
    isPrimerPrepReadOnly,
  });
  const comparableLastFailedValues = toPrimerPrepComparableValues(lastFailedValuesRef.current, { isPrimerPrepReadOnly });
  const hasUnsavedChanges =
    canEditPrimerPrepForm &&
    comparableLastSubmittedValues != null &&
    !isEqual(comparableLastSubmittedValues, comparableWatchedValues);

  const submitForm = useCallback(() => {
    if (!canEditPrimerPrepForm || updateWorkbookMutation.isPending) {
      return;
    }

    void handleSubmit(values => {
      const currentComparable = toPrimerPrepComparableValues(values, { isPrimerPrepReadOnly });
      const submittedComparable = toPrimerPrepComparableValues(lastSubmittedValuesRef.current, { isPrimerPrepReadOnly });
      if (submittedComparable != null && currentComparable != null && isEqual(submittedComparable, currentComparable)) {
        return;
      }

      updateWorkbookMutation.mutate(values);
    })();
  }, [handleSubmit, canEditPrimerPrepForm, updateWorkbookMutation, isPrimerPrepReadOnly]);
  const warnOnUnload =
    canEditPrimerPrepForm && (hasUnsavedChanges || updateWorkbookMutation.isPending || !!saveErrorMessage);
  const showSavingIndicator = canEditPrimerPrepForm && updateWorkbookMutation.isPending;
  const showSaveError =
    canEditPrimerPrepForm &&
    !!saveErrorMessage &&
    comparableLastFailedValues != null &&
    isEqual(comparableLastFailedValues, comparableWatchedValues);

  // Warn the user before leaving the page while edits are still in flight.
  useEffect(() => {
    if (!warnOnUnload) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [warnOnUnload]);

  const [opentronsMode, setOpentronsMode] = useState<'standard' | 'flex'>('flex');
  const [selectedDilutedPlateTab, setSelectedDilutedPlateTab] = useState(0);
  const [selectedOpentronsRunTab, setSelectedOpentronsRunTab] = useState(0);
  const numChipsField = register('numChips', {
    valueAsNumber: true,
    min: minimumNumChips,
    disabled: !canEditNumChips,
  });
  const defaultMmLotField = register('defaultMmLot');
  const primerStorageFindLocationField = register('primerStorageFindLocation');
  const primerStorageLeaveLocationField = register('primerStorageLeaveLocation');
  const prescreeningValue = !!watch('prescreening');
  const oneHealthOverrideValue = !!watch('oneHealthOverride');
  const primerStorageFindLocationValue = watch('primerStorageFindLocation');
  const primerStorageLeaveLocationValue = watch('primerStorageLeaveLocation');
  const clientPrimerStorageLocationsMissing =
    normalizeText(primerStorageFindLocationValue) == null || normalizeText(primerStorageLeaveLocationValue) == null;
  const serverPrimerStorageLocationsMissing =
    normalizeText(workbook?.primerStorageFindLocation) == null ||
    normalizeText(workbook?.primerStorageLeaveLocation) == null;
  const prescreeningToggleDisabled = true;
  const restrictOneHealthByPanel = !isOneHealthDefaultPanel && !oneHealthOverrideValue;
  const oneHealthOverrideToggleDisabled =
    isPrimerPrepReadOnly ||
    updateWorkbookMutation.isPending ||
    (prescreeningValue && !oneHealthOverrideValue) ||
    restrictOneHealthByPanel;
  const oneHealthOverrideDisabledReason = (() => {
    if (!isOneHealthDefaultPanel && !oneHealthOverrideValue) {
      return 'Available only for the standard 96 Gene Panel for One Health Framework.';
    }
    if (prescreeningValue && !oneHealthOverrideValue) {
      return 'Prescreening must be disabled to enable the OneHealth override.';
    }
    if (isPrimerPrepReadOnly) {
      return 'Primer prep settings are read only.';
    }
    if (updateWorkbookMutation.isPending) {
      return 'Update in progress.';
    }
    return undefined;
  })();
  const prescreeningDisabledReason = 'Prescreening is managed via the legacy Google Sheet selection.';

  useEffect(() => {
    if (!canManageAssignment) {
      autoPromotionAttemptRef.current = null;
      return;
    }
    if (workbookStatus !== LabWorkbookStatus.MEASURED) {
      autoPromotionAttemptRef.current = null;
      return;
    }
    if (assignedLabOrganisationId == null) {
      autoPromotionAttemptRef.current = null;
      return;
    }
    if (serverPrimerStorageLocationsMissing) {
      autoPromotionAttemptRef.current = null;
      return;
    }
    if (hasUnsavedChanges || updateWorkbookMutation.isPending || !!saveErrorMessage) {
      autoPromotionAttemptRef.current = null;
      return;
    }
    if (autoPromotionAttemptRef.current === assignedLabOrganisationId || assignLabMutation.isPending) {
      return;
    }
    autoPromotionAttemptRef.current = assignedLabOrganisationId;
    assignLabMutation.mutate(assignedLabOrganisationId);
  }, [
    assignedLabOrganisationId,
    assignLabMutation,
    canManageAssignment,
    hasUnsavedChanges,
    saveErrorMessage,
    serverPrimerStorageLocationsMissing,
    updateWorkbookMutation.isPending,
    workbookStatus,
  ]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const mapped = mapSummaryToForm(data);
    const lastSubmitted = lastSubmittedValuesRef.current;
    const dataMatchesLastSubmitted = lastSubmitted ? isEqual(lastSubmitted, mapped) : false;

    if (hasUnsavedChanges) {
      return;
    }

    if (!dataMatchesLastSubmitted || lastSubmittedValuesRef.current == null) {
      reset(mapped);
    }
    lastSubmittedValuesRef.current = cloneFormValues(mapped);
  }, [data, reset, hasUnsavedChanges]);

  // Uncomment if showing the "Diluted Plate Positions (with blanks)" section again.
  // const withBlanksColumns = useMemo<Array<TableColumn<LabPrimerPrepWithBlanksRow>>>(
  //   () => [
  //     { key: 'order', field: 'order', header: '#', width: 90, align: 'left' },
  //     { key: 'assay', field: 'assay', header: 'Selected assay' },
  //     { key: 'sourcePlate', field: 'sourcePlate', header: 'Source plate', width: 140 },
  //     { key: 'sourceWell', field: 'sourceWell', header: 'Source well', width: 140, monospace: true },
  //   ],
  //   [],
  // );

  // const doubleDippingColumns = useMemo<Array<TableColumn<LabPrimerPrepDoubleDippingRow>>>(
  //   () => [
  //     { key: 'order', field: 'order', header: '#', width: 90 },
  //     { key: 'duplicateIndex', field: 'duplicateIndex', header: 'Replicate', width: 120 },
  //     { key: 'assay', field: 'assay', header: 'Assay' },
  //     { key: 'sourcePlate', field: 'sourcePlate', header: 'Source plate', width: 140 },
  //     { key: 'sourceWell', field: 'sourceWell', header: 'Source well', width: 140, monospace: true },
  //   ],
  //   [],
  // );

  const compactedColumns = useMemo<Array<TableColumn<LabPrimerPrepCompactedRow>>>(
    () => [
      // { key: 'order', field: 'order', header: '#', width: 90 },
      { key: 'assay', field: 'assay', header: 'Assay', width: 110 },
      {
        key: 'sourcePlate',
        field: 'sourcePlate',
        header: 'Source plate',
        width: 150,
        align: 'right',
      },
      {
        key: 'sourceWell',
        field: 'sourceWell',
        header: 'Source well',
        width: 150,
        monospace: true,
      },
      { key: 'targetPlate', field: 'targetPlate', header: 'Dest. plate', width: 150, align: 'right' },
      { key: 'targetWell', field: 'targetWell', header: 'Dest.', width: 150, monospace: true },
      /* { key: 'plateRowColumn', field: 'plateRowColumn', header: 'Plate row column', width: 180, monospace: true },*/
    ],
    [],
  );

  const formatFixed2 = useCallback((value: unknown) => {
    if (value == null) return '';
    const num = Number(value);
    return Number.isNaN(num) ? String(value) : num.toFixed(2);
  }, []);

  const opentronsColumns = useMemo<Array<TableColumn<LabPrimerPrepOpentronsRow>>>(
    () => [
      { key: 'sourceLabware', field: 'sourceLabware', header: 'Source labware', width: 180 },
      { key: 'sourcePlate', field: 'sourcePlate', header: 'Source plate', width: 140, align: 'right' },
      { key: 'sourceWell', field: 'sourceWell', header: 'Source well', width: 140, monospace: true },
      {
        key: 'sourceAspirationHeightMm',
        field: 'sourceAspirationHeightMm',
        header: 'Aspiration height (mm)',
        width: 200,
        format: value => formatFixed2(value),
      },
      { key: 'destinationLabware', field: 'destinationLabware', header: 'Destination labware', width: 200 },
      { key: 'destinationSlot', field: 'destinationSlot', header: 'Destination slot', width: 160, align: 'right' },
      { key: 'destinationWell', field: 'destinationWell', header: 'Destination well', width: 160, monospace: true },
      { key: 'volume', field: 'volume', header: 'Volume (µL)', width: 160, format: value => formatFixed2(value) },
    ],
    [formatFixed2],
  );

  // const getWithBlanksRowId = useCallback((row: LabPrimerPrepWithBlanksRow) => String(row.order), [],);

  const getCompactedRowId = useCallback((row: LabPrimerPrepCompactedRow) => String(row.order), []);

  // const getDoubleDippingRowId = useCallback(
  //   (row: LabPrimerPrepDoubleDippingRow) => `${row.order}-${row.duplicateIndex}`,
  //   [],
  // );

  const getOpentronsRowId = useCallback((row: LabPrimerPrepOpentronsRow) => {
    const sourcePlate = row.sourcePlate ?? 'plate';
    const sourceWell = row.sourceWell ?? 'well';
    return `${String(sourcePlate)}-${sourceWell}-${row.destinationWell}`;
  }, []);

  const opentronsRunGroups = useMemo<OpentronsRunGroup[]>(() => {
    if (!primerPrep) {
      return [];
    }
    const uniqueTargetPlates = Array.from(new Set(primerPrep.compacted.map(r => r.targetPlate))).sort((a, b) => a - b);
    if (uniqueTargetPlates.length === 0) {
      return [];
    }

    const rowsByTargetPlate = new Map<number, number[]>();
    primerPrep.compacted.forEach((row, idx) => {
      const arr = rowsByTargetPlate.get(row.targetPlate);
      if (arr) arr.push(idx);
      else rowsByTargetPlate.set(row.targetPlate, [idx]);
    });

    const computeBatchSlots = (plates: number[]) => {
      const batchRowIdxs = plates.flatMap(p => rowsByTargetPlate.get(p) ?? []);
      const batchRows = batchRowIdxs.map(i => primerPrep.compacted[i]);
      const uniqueSourcePlates = new Set(batchRows.map(r => r.sourcePlate));
      uniqueSourcePlates.delete(null as unknown as number); // just in case
      const sourceSlots = uniqueSourcePlates.size; // up to 7 allowed physically
      const destinationSlots = plates.length; // one slot per destination plate in batch
      // Assume one tip per transfer; 96 tips per slot. We may fine-tune if lab reuses tips.
      const tipSlots = Math.max(1, Math.ceil(batchRowIdxs.length / 96));
      const total = sourceSlots + destinationSlots + tipSlots;
      return { total, sourceSlots, destinationSlots, tipSlots, batchRowIdxs };
    };

    const batches: number[][] = [];
    let current: number[] = [];
    for (const plate of uniqueTargetPlates) {
      const tentative = [...current, plate];
      const { total } = computeBatchSlots(tentative);
      if (total <= 11) {
        current = tentative;
      } else {
        if (current.length === 0) {
          // Even a single plate shouldn't exceed; but guard just in case
          current = [plate];
        }
        batches.push(current);
        current = [plate];
      }
    }
    if (current.length > 0) batches.push(current);

    return batches.map(batch => {
      const { batchRowIdxs } = computeBatchSlots(batch);
      const rowIndices = [...batchRowIdxs].sort((a, b) => a - b);
      return { label: formatPlateRunLabel(batch), rowIndices };
    });
  }, [primerPrep]);

  const opentronsRunRows = useMemo(() => {
    if (!primerPrep || opentronsRunGroups.length === 0) {
      return {
        standard: [] as LabPrimerPrepOpentronsRow[][],
        flex: [] as LabPrimerPrepOpentronsRow[][],
      };
    }
    const pickRows = (rows: LabPrimerPrepOpentronsRow[]) =>
      opentronsRunGroups.map(group =>
        group.rowIndices.filter(index => index >= 0 && index < rows.length).map(index => rows[index]),
      );
    return {
      standard: pickRows(primerPrep.opentrons.rows),
      flex: pickRows(primerPrep.opentronsFlex.rows),
    };
  }, [opentronsRunGroups, primerPrep]);

  useEffect(() => {
    if (opentronsRunGroups.length === 0) {
      if (selectedOpentronsRunTab !== 0) {
        setSelectedOpentronsRunTab(0);
      }
      return;
    }
    if (selectedOpentronsRunTab >= opentronsRunGroups.length) {
      setSelectedOpentronsRunTab(0);
    }
  }, [opentronsRunGroups, selectedOpentronsRunTab]);

  const metadataItems = useMemo<Array<{ label: string; value: string | number }>>(() => {
    if (!primerPrep) {
      return [];
    }
    const defaultMastermixLot = workbook?.defaultMmLot?.trim();
    const displayMastermixLot = defaultMastermixLot && defaultMastermixLot.length > 0 ? defaultMastermixLot : '—';
    const masterWaterVolume = primerPrep.metadata.masterWaterVolume;
    const masterWater = masterWaterVolume != null ? `${masterWaterVolume.toFixed(2)} µL` : '—';
    return [
      { label: 'SmartChip TB Green Gene Expression Master Mix Lot', value: displayMastermixLot },
      { label: 'Configuration', value: primerPrep.configurationId },
      { label: 'Opentrons H₂O', value: masterWater },
    ];
  }, [primerPrep, workbook?.defaultMmLot]);

  const activeOpentronsRunRows = opentronsMode === 'standard' ? opentronsRunRows.standard : opentronsRunRows.flex;

  if (!viewerAccess.canViewPrimerPrep) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>
          You do not have access to the primer preparation step for this lab workbook.
        </AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center minH="240px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : 'Failed to load workbook';
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
    );
  }

  if (!workbook || !primerPrep) {
    return null;
  }

  const assignmentOptionsWithCurrent =
    assignedLabOrganisationId != null && assignedLabOrganisation
      ? assignmentOptions.some(option => option.id === assignedLabOrganisationId)
        ? assignmentOptions
        : [assignedLabOrganisation, ...assignmentOptions]
      : assignmentOptions;
  const assignmentMissing = assignedLabOrganisationId == null;
  const statusAllowsAssignment =
    workbook.status !== LabWorkbookStatus.DRAFT && workbook.status !== LabWorkbookStatus.SAMPLE_SELECTION;
  const statusRequiresStorageDetails = shouldRequirePrimerStorageForAssignment(workbook.status, isPrimerPrepReadOnly);
  const assignmentBlockedByStatus = assignmentMissing && !statusAllowsAssignment;
  const assignmentBlockedByStorage = statusRequiresStorageDetails && clientPrimerStorageLocationsMissing;
  const assignmentBlockedByPrimerStorageSave =
    statusRequiresStorageDetails &&
    !assignmentBlockedByStorage &&
    (hasUnsavedChanges ||
      updateWorkbookMutation.isPending ||
      !!saveErrorMessage ||
      serverPrimerStorageLocationsMissing);
  const assignmentSelectDisabled =
    assignmentBlockedByStatus ||
    assignmentBlockedByStorage ||
    assignmentBlockedByPrimerStorageSave ||
    assignLabMutation.isPending ||
    (assignmentMissing && assignmentOptionsLoading);
  let assignmentDisabledReason: string | null = null;
  if (assignmentBlockedByStatus) {
    assignmentDisabledReason = 'Submit at least one chip before assigning a lab.';
  } else if (assignmentBlockedByStorage) {
    assignmentDisabledReason = 'Fill "Where to find" and "Where to leave" primers before assigning a lab.';
  } else if (assignmentBlockedByPrimerStorageSave) {
    if (saveErrorMessage) {
      assignmentDisabledReason = 'Fix the primer storage save error before assigning a lab.';
    } else {
      assignmentDisabledReason = 'Wait for primer storage details to finish saving before assigning a lab.';
    }
  } else if (assignmentMissing && assignmentOptionsLoading) {
    assignmentDisabledReason = 'Loading available lab organisations...';
  } else if (assignLabMutation.isPending) {
    assignmentDisabledReason = 'Updating assignment...';
  }
  if (assignmentSelectDisabled && !assignmentDisabledReason) {
    assignmentDisabledReason = 'Lab assignment temporarily unavailable.';
  }
  const showAssignmentWarning = canManageAssignment && assignmentMissing && !assignmentSelectDisabled;

  // Header shows the status badge; no local color mapping needed here.
  const labSheetUrl = sampleSheet ? sampleSheet.labSheetUrl : null;
  const channelUrl = workbook.channelUrl;
  const workbookName = workbook.name ? workbook.name.trim() : '';
  const baseHeaderTitle = 'Prepare Primer Plates';
  const headerTitle = workbookName ? `${workbookName} — ${baseHeaderTitle}` : baseHeaderTitle;

  return (
    <VStack align="stretch" spacing={8} pb={8}>
      <LabPageHeader
        title={headerTitle}
        labSheetUrl={labSheetUrl}
        channelUrl={channelUrl}
        control={
          canManageAssignment ? (
            <Tooltip
              label={assignmentDisabledReason ?? ''}
              isDisabled={!assignmentSelectDisabled || !assignmentDisabledReason}
              shouldWrapChildren
            >
              <Box as="span" display="inline-block" minW="240px">
                <Select
                  aria-label="Assign laboratory"
                  value={assignedLabOrganisationId != null ? String(assignedLabOrganisationId) : ''}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    const value = event.target.value;
                    const nextId = value === '' ? null : Number(value);
                    if (nextId !== null && !Number.isFinite(nextId)) {
                      return;
                    }
                    assignLabMutation.mutate(nextId);
                  }}
                  isDisabled={assignmentSelectDisabled}
                  w="100%"
                >
                  {assignmentMissing ? <option value="">Assign laboratory...</option> : null}
                  {assignmentOptionsWithCurrent.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Tooltip>
          ) : assignedLabOrganisation ? (
            <Text fontWeight="semibold" color="gray.700">
              {assignedLabOrganisation.name}
            </Text>
          ) : null
        }
      />
      {assignmentOptionsError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{assignmentOptionsError}</AlertDescription>
        </Alert>
      ) : null}
      {showAssignmentWarning ? (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>Assign a lab organisation before preparing primers.</AlertDescription>
        </Alert>
      ) : null}
      {workbook.status === LabWorkbookStatus.DRAFT || workbook.status === LabWorkbookStatus.SAMPLE_SELECTION ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Submit at least one chip&apos;s worth of samples before preparing primers.
          </AlertDescription>
        </Alert>
      ) : null}
      {workbook.status === LabWorkbookStatus.SUBMITTED ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Complete the DNA QC step before marking primers prepped.
          </AlertDescription>
        </Alert>
      ) : null}
      {isPrimerPrepReadOnly && workbook.status !== LabWorkbookStatus.LEGACY ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Primer prep settings are read only after marking primers prepped. Resistomap and provider admins can still
            adjust the chip count; doing so on a completed workbook reopens it to running.
          </AlertDescription>
        </Alert>
      ) : null}

      <Box
        as="form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          submitForm();
        }}
        borderWidth="1px"
        borderRadius="md"
        p={6}
        bg="white"
      >
        <input type="hidden" {...defaultMmLotField} />
        <Heading size="md" mb={4}>
          Configuration &amp; Storage
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          <FormControl>
            <FormLabel htmlFor="num-chips">Number of chips</FormLabel>
            <Input
              id="num-chips"
              type="number"
              min={minimumNumChips}
              step={1}
              isDisabled={!canEditNumChips}
              {...numChipsField}
              onBlur={event => {
                void numChipsField.onBlur(event);
                submitForm();
              }}
            />
          </FormControl>
          <VStack align="stretch" spacing={4}>
            <Controller
              name="prescreening"
              control={control}
              render={({ field }) => (
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="prescreening-toggle" mb="0">
                    <HStack spacing={2} align="center">
                      <Text as="span">Prescreening</Text>
                    </HStack>
                  </FormLabel>
                  <Switch
                    id="prescreening-toggle"
                    isChecked={field.value}
                    onChange={event => {
                      field.onChange(event.target.checked);
                      submitForm();
                    }}
                    isDisabled={prescreeningToggleDisabled}
                    title={prescreeningDisabledReason}
                  />
                  <InfoTooltipIcon header="Prescreening" placement="right">
                    <Text fontSize="sm">
                      Legacy Google Sheet selection. Overrides the SmartChip configuration to 384:4 regardless of the
                      selected gene panel. This affects the diluted‑plate layout, the pipetting (compacted) table, and
                      the Opentrons/Flex CSV.
                    </Text>
                  </InfoTooltipIcon>
                </FormControl>
              )}
            />
            <Controller
              name="oneHealthOverride"
              control={control}
              render={({ field }) => (
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="onehealth-toggle" mb="0">
                    <HStack spacing={2} align="center">
                      <Text as="span">OneHealth override</Text>
                    </HStack>
                  </FormLabel>
                  <Switch
                    id="onehealth-toggle"
                    isChecked={field.value}
                    onChange={event => {
                      field.onChange(event.target.checked);
                      submitForm();
                    }}
                    isDisabled={oneHealthOverrideToggleDisabled}
                    title={oneHealthOverrideDisabledReason}
                  />
                  <InfoTooltipIcon header="OneHealth override" placement="right">
                    <Text fontSize="sm">
                      Use fixed OneHealth 96‑gene panel ordering to drive primer‑prep contents and ordering. This
                      affects the diluted‑plate layout, the pipetting (compacted) table, and the Opentrons/Flex CSV.
                      Available only for the standard 96 Gene Panel for One Health Framework.
                    </Text>
                  </InfoTooltipIcon>
                </FormControl>
              )}
            />
          </VStack>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <FormControl>
            <FormLabel>Where to find primers</FormLabel>
            <Textarea
              placeholder="Location details"
              minH="96px"
              {...primerStorageFindLocationField}
              onBlur={event => {
                void primerStorageFindLocationField.onBlur(event);
                submitForm();
              }}
              isDisabled={isPrimerPrepReadOnly || updateWorkbookMutation.isPending}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Where to leave primers</FormLabel>
            <Textarea
              placeholder="Return location"
              minH="96px"
              {...primerStorageLeaveLocationField}
              onBlur={event => {
                void primerStorageLeaveLocationField.onBlur(event);
                submitForm();
              }}
              isDisabled={isPrimerPrepReadOnly || updateWorkbookMutation.isPending}
            />
          </FormControl>
        </SimpleGrid>

        <Flex justify="flex-end" mt={6} minH="24px" align="center">
          {showSaveError ? (
            <Text color="red.600" fontSize="sm" fontWeight="medium">
              {saveErrorMessage}
            </Text>
          ) : showSavingIndicator ? (
            <HStack spacing={2}>
              <Spinner size="xs" />
              <Text color="gray.600" fontSize="sm" fontWeight="medium">
                Saving...
              </Text>
            </HStack>
          ) : hasUnsavedChanges ? (
            <Text color="orange.500" fontSize="sm" fontWeight="medium">
              Unsaved changes
            </Text>
          ) : (
            <Text color="gray.500" fontSize="sm">
              {isPrimerPrepReadOnly ? 'Read only' : 'All changes saved'}
            </Text>
          )}
        </Flex>
      </Box>

      {primerPrep.metadata.missingAssays.length > 0 && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            The following assays were not found in the 2024 primer inventory and have been omitted:{' '}
            {primerPrep.metadata.missingAssays.join(', ')}
          </AlertDescription>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        {metadataItems.map(item => (
          <Stat key={item.label} borderWidth="1px" borderRadius="md" p={4} bg="white">
            <StatLabel>{item.label}</StatLabel>
            <StatNumber>{item.value}</StatNumber>
          </Stat>
        ))}
      </SimpleGrid>

      <Box>
        <Heading size="md" mb={4}>
          Diluted Plate Layout
        </Heading>
        <Tabs
          colorScheme="purple"
          variant="enclosed"
          index={selectedDilutedPlateTab}
          onChange={setSelectedDilutedPlateTab}
        >
          <TabList>
            {primerPrep.dilutedPlate.plates.map(plate => (
              <Tab key={plate.plate}>Plate {plate.plate}</Tab>
            ))}
          </TabList>
          <TabPanels mt={4}>
            {primerPrep.dilutedPlate.plates.map(plate => (
              <TabPanel key={plate.plate} px={0}>
                <SmartChipGridView
                  title={`Plate ${plate.plate}`}
                  grid={{
                    rowLabels: primerPrep.dilutedPlate.rowLabels,
                    columnLabels: primerPrep.dilutedPlate.columnLabels,
                    values: plate.grid,
                    caption: null,
                  }}
                  banding={{ orientation: 'column', step: 1, colors: ['#EECDCD', '#789DE5'] }}
                />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Box>

      {/* <Box>
        <Heading size="md" mb={4}>
          Diluted Plate Positions (with blanks)
        </Heading>
        <ReadOnlyGrid
          columnDefs={withBlanksColumns}
          rowData={primerPrep.withBlanks}
          height={360}
          getRowId={getWithBlanksRowId}
        />
      </Box> */}

      {/* {primerPrep.doubleDipping && primerPrep.doubleDipping.length > 0 && (
        <Box>
          <Heading size="md" mb={4}>
            Double Dipping (6:256)
          </Heading>
          <ReadOnlyTable
            columns={doubleDippingColumns}
            rows={primerPrep.doubleDipping}
            getRowId={getDoubleDippingRowId}
          />
        </Box>
      )} */}

      <Box>
        <Heading size="md" mb={4}>
          {(() => {
            const plateIdx = Math.min(selectedDilutedPlateTab, Math.max(0, primerPrep.dilutedPlate.plates.length - 1));
            const plateNo = primerPrep.dilutedPlate.plates[plateIdx]?.plate ?? 1;
            return `Pipeting to Diluted Plate — Plate ${plateNo}`;
          })()}
        </Heading>
        {(() => {
          const plateIdx = Math.min(selectedDilutedPlateTab, Math.max(0, primerPrep.dilutedPlate.plates.length - 1));
          const plateNo = primerPrep.dilutedPlate.plates[plateIdx]?.plate ?? 1;
          const filtered = primerPrep.compacted.filter(r => r.targetPlate === plateNo);
          return <ReadOnlyTable columns={compactedColumns} rows={filtered} getRowId={getCompactedRowId} />;
        })()}
      </Box>

      <Box>
        <Flex align="center" justify="space-between" mb={4}>
          <Heading size="md">
            {opentronsMode === 'flex' ? 'Opentrons Flex Instructions' : 'Opentrons Instructions'}
          </Heading>
          <HStack spacing={3}>
            <ResistomapSwitch
              options={
                [
                  { value: 'flex', label: 'Flex' },
                  { value: 'standard', label: 'Standard' },
                ] as const
              }
              value={opentronsMode}
              onChange={setOpentronsMode}
              widths={[30, 63]}
            />
            <Button
              onClick={() => {
                const hasRunGroups = opentronsRunGroups.length > 0 && activeOpentronsRunRows.length > 0;
                if (hasRunGroups) {
                  const selectedRows =
                    activeOpentronsRunRows[Math.min(selectedOpentronsRunTab, activeOpentronsRunRows.length - 1)] ?? [];
                  const { csv } = buildOpentronsCsvForRows(selectedRows, opentronsMode);
                  const label =
                    opentronsRunGroups[Math.min(selectedOpentronsRunTab, opentronsRunGroups.length - 1)]?.label;
                  const slug = label
                    ? label
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '')
                    : 'run';
                  const name =
                    (opentronsMode === 'standard' ? 'primer-prep-opentrons' : 'primer-prep-opentrons-flex') +
                    `-${slug}.csv`;
                  downloadCsv(csv, name);
                  return;
                }
                // Fallback: no run grouping — download the full CSV from the backend
                const d = opentronsMode === 'standard' ? primerPrep.opentrons : primerPrep.opentronsFlex;
                downloadCsv(d.csv, d.suggestedFileName);
              }}
            >
              Download CSV
            </Button>
          </HStack>
        </Flex>
        {opentronsRunGroups.length > 0 && activeOpentronsRunRows.length > 0 ? (
          <Tabs
            colorScheme="purple"
            variant="enclosed"
            index={Math.min(selectedOpentronsRunTab, opentronsRunGroups.length - 1)}
            onChange={setSelectedOpentronsRunTab}
          >
            <TabList>
              {opentronsRunGroups.map(group => (
                <Tab key={group.label}>{group.label}</Tab>
              ))}
            </TabList>
            <TabPanels mt={4}>
              {opentronsRunGroups.map((group, index) => (
                <TabPanel key={group.label} px={0}>
                  <ReadOnlyTable
                    columns={opentronsColumns}
                    rows={activeOpentronsRunRows[index] ?? []}
                    getRowId={getOpentronsRowId}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        ) : (
          <ReadOnlyTable
            columns={opentronsColumns}
            rows={opentronsMode === 'standard' ? primerPrep.opentrons.rows : primerPrep.opentronsFlex.rows}
            getRowId={getOpentronsRowId}
          />
        )}
      </Box>
    </VStack>
  );
}
